import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { Address, CartLine, Customer, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { VariantDetails } from '../../relewise/types/mapped';
import {
    DatalayerEcommerceObject,
    DatalayerEventName,
    DatalayerFacebookContent,
    DatalayerIdentityObject,
    DatalayerItem
} from '../types/datalayer';

const AWAIT_IDENTITY_TIMEOUT_MS = 4000;
const LOG_INITIAL_IDENTITY_ERROR_TIMEOUT_MS = 5000;
const DATALAYER_COMMERCE_EVENTS = [
    'add_to_wishlist',
    'view_item_list',
    'view_item',
    'add_to_cart',
    'view_cart',
    'apply_coupon',
    'go_to_checkout',
    'begin_checkout',
    'add_contact_info',
    'add_shipping_info',
    'add_delivery_info',
    'add_billing_info',
    'add_payment_info',
    'purchase'
];

interface CategoryInput {
    RecordId?: number;
    Name?: string;
}

interface SalesLineInput {
    ItemId?: string;
    ProductId?: number;
    Barcode?: string;
    TotalAmount?: number;
    DiscountAmount?: number;
    Quantity?: number;
}

export class DatalayerService {
    private readonly request: IRequestContext;
    private readonly telemetry: ITelemetry;
    private readonly isDev = process.env.NODE_ENV === 'development';
    private showTrackingLogs = false;

    private static hasIdentityMonitorSucceeded = false;
    private static identityMonitorTimeoutId: number | undefined;

    // Ensures that identity has been pushed before pushing other events
    // Shared across all instances of this class
    private static resolveIdentityReady: (() => void) | undefined;
    private static identityReadyPromise = DatalayerService.createIdentityReadyPromise();
    private static createIdentityReadyPromise(): Promise<void> {
        return new Promise(resolve => {
            DatalayerService.resolveIdentityReady = resolve;
        });
    }
    private static waitForIdentityReady(): Promise<void> {
        return Promise.race([
            DatalayerService.identityReadyPromise,
            new Promise<void>(resolve => {
                setTimeout(resolve, AWAIT_IDENTITY_TIMEOUT_MS);
            })
        ]);
    }
    private static markIdentityReady(): void {
        DatalayerService.hasIdentityMonitorSucceeded = true;
        if (DatalayerService.identityMonitorTimeoutId !== undefined) {
            window.clearTimeout(DatalayerService.identityMonitorTimeoutId);
            DatalayerService.identityMonitorTimeoutId = undefined;
        }
        DatalayerService.resolveIdentityReady?.();
        DatalayerService.resolveIdentityReady = undefined;
    }

    constructor(request: IRequestContext, telemetry: ITelemetry) {
        this.request = request;
        this.telemetry = telemetry;
        this.showTrackingLogs = request.app.config?.showTrackingLogs || this.isDev;
    }

    private get pageName(): string {
        return this.request.telemetryPageName ?? window.location.pathname;
    }

    public startInitialIdentityMonitor(): void {
        if (typeof window === 'undefined') {
            return;
        }
        if (DatalayerService.hasIdentityMonitorSucceeded || DatalayerService.identityMonitorTimeoutId !== undefined) {
            return;
        }

        DatalayerService.identityMonitorTimeoutId = window.setTimeout(() => {
            DatalayerService.identityMonitorTimeoutId = undefined;
            if (DatalayerService.hasIdentityMonitorSucceeded) {
                return;
            }
            this.logInitialIdentityTimeout();
        }, LOG_INITIAL_IDENTITY_ERROR_TIMEOUT_MS);
    }

    private logInitialIdentityTimeout() {
        const message = `[Datalayer Service] Identity event was not pushed within ${LOG_INITIAL_IDENTITY_ERROR_TIMEOUT_MS}ms on page ${this.pageName}`;
        this.telemetry.error(message);
        console.error(message);
    }

    private logInitialIdentitySuccess() {
        const message = `[Datalayer Service] Identity event pushed successfully on page ${this.pageName}`;
        this.telemetry.warning(message);
    }

    private logEvent(name: string, payload: Record<string, unknown>) {
        if (this.showTrackingLogs) {
            console.log(`[Datalayer Service] ${name}:`, payload);
        }
    }

    private logMalformedEvent(event: DatalayerEventName, errors: string[], payload: Record<string, unknown>): void {
        const baseMessage = `[Datalayer Service] Malformed ${event} event on page ${this.pageName}: ${errors.join(', ')}`;
        console.error(baseMessage, {
            ...(this.showTrackingLogs ? { payload } : {})
        });
        this.telemetry.error(baseMessage);
    }

    private async getSHA256Hash(input?: string): Promise<string> {
        if (!input || typeof window === 'undefined' || !window.crypto?.subtle) {
            return '';
        }
        const textAsBuffer = new TextEncoder().encode(input);
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', textAsBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(item => item.toString(16).padStart(2, '0')).join('');
    }

    private buildItemId(styleId: string, colorName: string, size: string): string {
        return styleId.concat(colorName, size).split(' ').join('_').split('#').join('').split('/').join('');
    }

    private getFbp(): string | undefined {
        return this.request.cookies.get('_fbp')?.value?.toString();
    }

    private async buildEventBase(email?: string): Promise<Record<string, unknown>> {
        const emailToHash = email ?? (this.request.user.isAuthenticated ? this.request.user.emailAddress : undefined);
        const externalId = emailToHash ? await this.getSHA256Hash(emailToHash) : undefined;
        const fbp = this.getFbp();

        return {
            ...(externalId ? { external_id: externalId } : {}),
            client_ip_address: this.request.clientIPAddress ?? '',
            user_agent: typeof window !== 'undefined' ? navigator.userAgent : '',
            action_source: 'website',
            ...(fbp ? { fbp } : {})
        };
    }

    private async pushAfterIdentity(
        event: DatalayerEventName,
        eventObject: Record<string, unknown>,
        ecommerce?: DatalayerEcommerceObject
    ): Promise<void> {
        await DatalayerService.waitForIdentityReady();
        this.push(event, eventObject, ecommerce);
    }

    private validatePayload(event: DatalayerEventName, payload: Record<string, unknown>): string[] {
        const errors: string[] = [];

        if (event === 'search' && !payload.search_term) {
            errors.push('missing search_term');
        }

        if (DATALAYER_COMMERCE_EVENTS.includes(event)) {
            const ecommerce = payload.ecommerce as DatalayerEcommerceObject | undefined;

            if (!ecommerce) {
                errors.push('missing ecommerce');
            }

            if (!ecommerce?.currency) {
                errors.push('missing ecommerce.currency');
            }

            if (!ecommerce?.items?.length) {
                errors.push('missing ecommerce.items');
            }
        }

        if (event === 'purchase') {
            const ecommerce = payload.ecommerce as DatalayerEcommerceObject | undefined;
            if (!ecommerce?.transaction_id) {
                errors.push('missing ecommerce.transaction_id');
            }
        }

        return errors;
    }

    private push(event: DatalayerEventName, eventObject: Record<string, unknown>, ecommerce?: DatalayerEcommerceObject): void {
        if (typeof window === 'undefined') {
            return;
        }

        window.dataLayer = window.dataLayer || [];

        const payload = { ...eventObject, event, ...(ecommerce ? { ecommerce } : {}) };
        const validationErrors = this.validatePayload(event, payload);

        if (validationErrors.length > 0) {
            this.logMalformedEvent(event, validationErrors, payload);
            return;
        }

        if (ecommerce) {
            window.dataLayer.push({ ecommerce: null });
        }

        this.logEvent(event, payload);
        window.dataLayer.push(payload);
    }

    private mapPdpItem(
        masterProductItemId: string,
        colorName: string,
        size: string,
        variant: VariantDetails,
        brand: string | undefined
    ): { items: DatalayerItem[]; contents: DatalayerFacebookContent[]; contentIds: string[] } {
        const itemId = this.buildItemId(masterProductItemId, colorName, size);
        const discount =
            variant.hasDiscount && variant.basePrice !== undefined && variant.finalPrice !== undefined
                ? Number((variant.basePrice - variant.finalPrice).toFixed(2))
                : 0;

        const item: DatalayerItem = {
            item_id: itemId,
            item_name: variant.displayName,
            style_id: masterProductItemId,
            item_variant: colorName,
            size,
            index: 0,
            item_brand: brand,
            price: variant.finalPrice,
            discount,
            quantity: 1
        };
        const contents: DatalayerFacebookContent[] = [{ id: itemId, quantity: 1, item_price: variant.finalPrice }];
        return { items: [item], contents, contentIds: [itemId] };
    }

    public async pushIdentity(customer?: Customer): Promise<void> {
        if (typeof window === 'undefined') {
            DatalayerService.markIdentityReady();
            return;
        }
        try {
            const { isAuthenticated, emailAddress } = this.request.user;
            const email = customer?.Email ?? (isAuthenticated ? emailAddress : undefined);
            const primaryAddress = customer?.Addresses?.find(a => a.IsPrimary) ?? customer?.Addresses?.[0];
            const nameParts = primaryAddress?.Name?.split(' ');
            const firstName = nameParts?.[0] ?? customer?.FirstName;
            const lastName = (nameParts && nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined) ?? customer?.LastName;

            const [
                hashedEmail,
                hashedUserId,
                hashedFirstName,
                hashedLastName,
                hashedPhone,
                hashedStreet,
                hashedCity,
                hashedZipCode,
                hashedCountry
            ] = await Promise.all([
                this.getSHA256Hash(email),
                this.getSHA256Hash(customer?.AccountNumber),
                this.getSHA256Hash(firstName),
                this.getSHA256Hash(lastName),
                this.getSHA256Hash(customer?.Phone),
                this.getSHA256Hash(primaryAddress?.Street),
                this.getSHA256Hash(primaryAddress?.City),
                this.getSHA256Hash(primaryAddress?.ZipCode),
                this.getSHA256Hash(primaryAddress?.ThreeLetterISORegionName)
            ]);

            const identityObject: DatalayerIdentityObject = {
                user_type: customer || isAuthenticated ? 'member' : 'guest',
                email: hashedEmail,
                user_id: hashedUserId,
                address: {
                    ...(hashedFirstName ? { first_name: hashedFirstName } : {}),
                    ...(hashedLastName ? { last_name: hashedLastName } : {}),
                    ...(hashedPhone ? { phone: hashedPhone } : {}),
                    ...(hashedStreet ? { street: hashedStreet } : {}),
                    ...(hashedCity ? { city: hashedCity } : {}),
                    ...(hashedZipCode ? { zipcode: hashedZipCode } : {}),
                    ...(hashedCountry ? { country: hashedCountry } : {})
                },
                event: 'identity'
            };
            this.push('identity', identityObject);
            this.logInitialIdentitySuccess();
        } finally {
            DatalayerService.markIdentityReady();
        }
    }

    public async pushSignUpIfUserIsNew(): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }
        const userClaims = this.request.user.userClaims;
        if (userClaims?.IsNewUser !== 'true') {
            return;
        }
        const base = await this.buildEventBase();
        await this.pushAfterIdentity('sign_up', base);
    }

    public async pushSearch(): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }
        const query = this.request.query?.q;
        if (!query) {
            return;
        }
        const base = await this.buildEventBase();
        await this.pushAfterIdentity('search', { ...base, search_term: query });
    }

    public async pushAddToWishlist(item: DatalayerItem): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }
        const base = await this.buildEventBase();
        const contents: DatalayerFacebookContent[] = [{ id: item.item_id, quantity: 1, item_price: item.price }];
        const ecommerce: DatalayerEcommerceObject = {
            currency: this.request.channel?.Currency,
            value: item.price,
            items: [item],
            content_type: 'product',
            contents,
            content_ids: [item.item_id]
        };
        await this.pushAfterIdentity('add_to_wishlist', base, ecommerce);
    }

    public async pushViewItemList(items: DatalayerItem[], category: CategoryInput | undefined): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }
        const query = this.request.query?.q;
        const { itemListId, itemListName } = this.getSearchResultsItemListIdentifiers(query) ?? {};
        const base = await this.buildEventBase();
        const value = items.reduce((sum, i) => sum + (i.price ?? 0), 0);
        const ecommerce: DatalayerEcommerceObject = {
            item_list_id: itemListId ?? category?.RecordId,
            item_list_name: itemListName ?? category?.Name,
            currency: this.request.channel?.Currency,
            value,
            items,
            content_type: 'product',
            content_ids: items.map(i => i.item_id)
        };
        await this.pushAfterIdentity('view_item_list', base, ecommerce);
    }

    private getSearchResultsItemListIdentifiers(searchTerm?: string): { itemListId: string; itemListName: string } | undefined {
        if (!searchTerm) {
            return;
        }
        return {
            itemListId: `search_${searchTerm.replace(/ /g, '_')}`,
            itemListName: `Search: ${searchTerm}`
        };
    }

    public async pushViewItem(
        masterProductItemId: string,
        colorName: string,
        size: string,
        variant: VariantDetails,
        brand: string | undefined
    ): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }
        const base = await this.buildEventBase();
        const { items, contents, contentIds } = this.mapPdpItem(masterProductItemId, colorName, size, variant, brand);
        const ecommerce: DatalayerEcommerceObject = {
            currency: this.request.channel?.Currency,
            value: variant.finalPrice,
            items,
            content_type: 'product',
            contents,
            content_ids: contentIds
        };
        await this.pushAfterIdentity('view_item', base, ecommerce);
    }

    public async pushAddToCart(
        masterProductItemId: string,
        colorName: string,
        size: string,
        variant: VariantDetails,
        brand: string | undefined
    ): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }
        const base = await this.buildEventBase();
        const { items, contents, contentIds } = this.mapPdpItem(masterProductItemId, colorName, size, variant, brand);
        const ecommerce: DatalayerEcommerceObject = {
            currency: this.request.channel?.Currency,
            value: variant.finalPrice,
            items,
            content_type: 'product',
            contents,
            content_ids: contentIds
        };
        await this.pushAfterIdentity('add_to_cart', base, ecommerce);
    }

    private mapOrderItems(
        salesLines: SalesLineInput[],
        products: SimpleProduct[]
    ): { items: DatalayerItem[]; contents: DatalayerFacebookContent[]; contentIds: string[] } {
        const contents: DatalayerFacebookContent[] = [];
        const items = salesLines.map(line => {
            const product = products.find(p => p.RecordId === line.ProductId);
            const dimensions = product?.Dimensions ?? [];
            const size = dimensions[0]?.DimensionValue?.DimensionId ?? '';
            const colorVariant = dimensions[1]?.DimensionValue?.DimensionId ?? '';
            const item: DatalayerItem = {
                style_id: line.ItemId ?? '',
                item_id: this.buildItemId(line.ItemId ?? '', colorVariant, size),
                item_name: product?.Name,
                item_brand: 'ONLY',
                item_variant: colorVariant,
                size,
                gtin: line.Barcode ?? undefined,
                price: line.TotalAmount,
                discount: line.DiscountAmount,
                quantity: line.Quantity
            };
            contents.push({ id: item.item_id, quantity: item.quantity ?? 1, item_price: item.price });
            return item;
        });
        return { items, contents, contentIds: items.map(i => i.item_id) };
    }

    private mapCartItems(
        cartLines: CartLine[],
        products: SimpleProduct[]
    ): { items: DatalayerItem[]; contents: DatalayerFacebookContent[]; contentIds: string[] } {
        const contents: DatalayerFacebookContent[] = [];
        const items = cartLines.map(line => {
            const product = products.find(p => p.RecordId === line.ProductId);
            const dimensions = product?.Dimensions ?? [];
            const size = dimensions[0]?.DimensionValue?.DimensionId ?? '';
            const colorVariant = dimensions[1]?.DimensionValue?.DimensionId ?? '';
            const item: DatalayerItem = {
                style_id: line.ItemId ?? '',
                item_id: this.buildItemId(line.ItemId ?? '', colorVariant, size),
                item_name: product?.Name,
                item_brand: 'ONLY',
                item_variant: colorVariant,
                size,
                gtin: line.Barcode ?? undefined,
                price: line.TotalAmount,
                discount: line.DiscountAmount,
                quantity: line.Quantity
            };
            contents.push({ id: item.item_id, quantity: item.quantity ?? 1, item_price: item.price });
            return item;
        });
        return { items, contents, contentIds: items.map(i => i.item_id) };
    }

    private async buildHashedGuestIdentityFields(
        email: string | undefined,
        shippingAddress: Address | undefined,
        billingAddress: Address | undefined
    ): Promise<{ hashedEmail: string; shippingHashes: Record<string, string>; billingHashes: Record<string, string> }> {
        const shippingParts = shippingAddress?.Name?.split(' ');
        const shippingFirst = shippingParts?.[0];
        const shippingLast = shippingParts && shippingParts.length > 1 ? shippingParts.slice(1).join(' ') : undefined;
        const billingParts = billingAddress && shippingAddress ? billingAddress.Name?.split(' ') : undefined;
        const billingFirst = billingParts?.[0];
        const billingLast = billingParts && billingParts.length > 1 ? billingParts.slice(1).join(' ') : undefined;

        const [hashedEmail, hFirst, hLast, hStreet, hCity, hZip, hPhone, hBFirst, hBLast] = await Promise.all([
            this.getSHA256Hash(email),
            this.getSHA256Hash(shippingFirst),
            this.getSHA256Hash(shippingLast),
            this.getSHA256Hash(shippingAddress?.Street),
            this.getSHA256Hash(shippingAddress?.City),
            this.getSHA256Hash(shippingAddress?.ZipCode),
            this.getSHA256Hash(shippingAddress?.Phone),
            this.getSHA256Hash(billingFirst),
            this.getSHA256Hash(billingLast)
        ]);

        const shippingHashes = shippingAddress
            ? {
                  ...(hFirst ? { first_name: hFirst } : {}),
                  ...(hLast ? { last_name: hLast } : {}),
                  ...(hStreet ? { street: hStreet } : {}),
                  ...(hCity ? { city: hCity } : {}),
                  ...(hZip ? { zipcode: hZip } : {}),
                  ...(hPhone ? { phone: hPhone } : {})
              }
            : {};

        const billingHashes =
            billingAddress && shippingAddress
                ? {
                      ...(hBFirst ? { first_name: hBFirst } : {}),
                      ...(hBLast ? { last_name: hBLast } : {})
                  }
                : {};

        return { hashedEmail, shippingHashes, billingHashes };
    }

    public async pushCheckoutIdentity(
        guestEmail: string | undefined,
        shippingAddress: Address | undefined,
        billingAddress: Address | undefined
    ): Promise<void> {
        if (typeof window === 'undefined') {
            DatalayerService.markIdentityReady();
            return;
        }
        try {
            const { hashedEmail, shippingHashes, billingHashes } = await this.buildHashedGuestIdentityFields(
                guestEmail,
                shippingAddress,
                billingAddress
            );

            const identityObject: DatalayerIdentityObject = {
                user_type: 'guest',
                email: hashedEmail,
                user_id: '',
                address: { ...shippingHashes, ...billingHashes },
                event: 'identity'
            };
            this.push('identity', identityObject);
        } finally {
            DatalayerService.markIdentityReady();
        }
    }
    public async pushOrderConfirmationIdentity(
        order: { ReceiptEmail?: string; ShippingAddress?: Address },
        customer?: Customer
    ): Promise<void> {
        if (typeof window === 'undefined') {
            DatalayerService.markIdentityReady();
            return;
        }
        if (customer) {
            await this.pushIdentity(customer);
            return;
        }
        try {
            const [hashedEmail, hashedPhone, hashedCountry] = await Promise.all([
                this.getSHA256Hash(order.ReceiptEmail),
                this.getSHA256Hash(order.ShippingAddress?.Phone),
                this.getSHA256Hash(order.ShippingAddress?.ThreeLetterISORegionName)
            ]);

            const identityObject: DatalayerIdentityObject = {
                user_type: 'guest',
                email: hashedEmail,
                user_id: '',
                address: {
                    ...(hashedPhone ? { phone: hashedPhone } : {}),
                    ...(hashedCountry ? { country: hashedCountry } : {})
                },
                event: 'identity'
            };
            this.push('identity', identityObject);
        } finally {
            DatalayerService.markIdentityReady();
        }
    }

    // Pushes an enriched identity event at each checkout step for guest users.
    // Does NOT call markIdentityReady — identity is already resolved by the header.
    public async pushCheckoutStepIdentity(
        guestEmail: string | undefined,
        shippingAddress: Address | undefined,
        billingAddress: Address | undefined
    ): Promise<void> {
        if (typeof window === 'undefined' || this.request.user.isAuthenticated) {
            return;
        }

        const { hashedEmail, shippingHashes, billingHashes } = await this.buildHashedGuestIdentityFields(
            guestEmail,
            shippingAddress,
            billingAddress
        );

        const identityObject: DatalayerIdentityObject = {
            user_type: 'guest',
            email: hashedEmail,
            user_id: '',
            address: { ...shippingHashes, ...billingHashes },
            event: 'identity'
        };
        this.push('identity', identityObject);
    }

    public async pushViewCart(cartLines: CartLine[], products: SimpleProduct[], totalAmount: number | undefined): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }
        const base = await this.buildEventBase();
        const { items, contents, contentIds } = this.mapCartItems(cartLines, products);
        const ecommerce: DatalayerEcommerceObject = {
            currency: this.request.channel?.Currency,
            value: totalAmount,
            items,
            content_type: 'product',
            contents,
            content_ids: contentIds
        };
        await this.pushAfterIdentity('view_cart', base, ecommerce);
    }

    public async pushApplyCoupon(cartLines: CartLine[], products: SimpleProduct[], totalAmount: number | undefined): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }
        const base = await this.buildEventBase();
        const { items, contents, contentIds } = this.mapCartItems(cartLines, products);
        const ecommerce: DatalayerEcommerceObject = {
            currency: this.request.channel?.Currency,
            value: totalAmount,
            items,
            content_type: 'product',
            contents,
            content_ids: contentIds
        };
        await this.pushAfterIdentity('apply_coupon', base, ecommerce);
    }

    public async pushGoToCheckout(cartLines: CartLine[], products: SimpleProduct[], totalAmount: number | undefined): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }
        const base = await this.buildEventBase();
        const { items, contents, contentIds } = this.mapCartItems(cartLines, products);
        const ecommerce: DatalayerEcommerceObject = {
            currency: this.request.channel?.Currency,
            value: totalAmount,
            items,
            content_type: 'product',
            contents,
            content_ids: contentIds
        };
        await this.pushAfterIdentity('go_to_checkout', base, ecommerce);
    }

    public async pushBeginCheckout(cartLines: CartLine[], products: SimpleProduct[], totalAmount: number | undefined): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }
        // Skip begin_checkout on payment verification redirect
        if (new URLSearchParams(window.location.search).get('redirectResult')) {
            return;
        }
        const base = await this.buildEventBase();
        const { items, contents, contentIds } = this.mapCartItems(cartLines, products);
        const ecommerce: DatalayerEcommerceObject = {
            currency: this.request.channel?.Currency,
            value: totalAmount,
            items,
            content_type: 'product',
            contents,
            content_ids: contentIds
        };
        await this.pushAfterIdentity('begin_checkout', base, ecommerce);
    }

    public async pushCheckoutStep(
        eventName: DatalayerEventName,
        cartLines: CartLine[],
        products: SimpleProduct[],
        totalAmount: number | undefined,
        email?: string
    ): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }
        const base = await this.buildEventBase(email);
        const { items, contents, contentIds } = this.mapCartItems(cartLines, products);
        const ecommerce: DatalayerEcommerceObject = {
            currency: this.request.channel?.Currency,
            value: totalAmount,
            items,
            content_type: 'product',
            contents,
            content_ids: contentIds
        };
        await this.pushAfterIdentity(eventName, base, ecommerce);
    }

    public async pushPurchase(
        salesLines: SalesLineInput[],
        products: SimpleProduct[],
        transactionId: string | number | undefined,
        totalAmount: number | undefined
    ): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }
        const base = await this.buildEventBase();
        const { items, contents, contentIds } = this.mapOrderItems(salesLines, products);
        const ecommerce: DatalayerEcommerceObject = {
            transaction_id: transactionId !== undefined ? String(transactionId) : undefined,
            currency: this.request.channel?.Currency,
            value: totalAmount,
            items,
            content_type: 'product',
            contents,
            content_ids: contentIds
        };
        await this.pushAfterIdentity('purchase', base, ecommerce);
    }

    public hasCheckoutEventFired(cartId: string, event: string): boolean {
        if (typeof window === 'undefined') {
            return false;
        }
        return window.sessionStorage.getItem(`dl_checkout_${cartId}_${event}`) === '1';
    }

    public markCheckoutEventFired(cartId: string, event: string): void {
        if (typeof window === 'undefined') {
            return;
        }
        window.sessionStorage.setItem(`dl_checkout_${cartId}_${event}`, '1');
    }

    private static readonly GUEST_IDENTITY_STORAGE_KEY = 'dl_guest_checkout_identity';

    public saveGuestCheckoutIdentity(email?: string, shippingAddress?: Address, billingAddress?: Address): void {
        if (typeof window === 'undefined') {
            return;
        }
        const data: Record<string, unknown> = {};
        if (email) {
            data.email = email;
        }
        if (shippingAddress) {
            data.shippingAddress = shippingAddress;
        }
        if (billingAddress) {
            data.billingAddress = billingAddress;
        }
        if (Object.keys(data).length) {
            window.localStorage.setItem(DatalayerService.GUEST_IDENTITY_STORAGE_KEY, JSON.stringify(data));
        }
    }

    public readGuestCheckoutIdentity(): { email?: string; shippingAddress?: Address; billingAddress?: Address } | undefined {
        if (typeof window === 'undefined') {
            return undefined;
        }
        try {
            const raw = window.localStorage.getItem(DatalayerService.GUEST_IDENTITY_STORAGE_KEY);
            return raw ? (JSON.parse(raw) as { email?: string; shippingAddress?: Address; billingAddress?: Address }) : undefined;
        } catch {
            return undefined;
        }
    }

    public clearGuestCheckoutIdentity(): void {
        if (typeof window === 'undefined') {
            return;
        }
        window.localStorage.removeItem(DatalayerService.GUEST_IDENTITY_STORAGE_KEY);
    }
}
